import { createClient } from "./supabase/client";

export type Conversation = {
  id: string;
  listing_id: string;
  buyer_id: string;
  seller_id: string;
  blocked_by: string | null;
  last_message_at: string | null;
  created_at: string;
  listing?: {
    title: string;
    slug: string | null;
  };
  buyer?: {
    display_name: string | null;
    avatar_url: string | null;
  };
  seller?: {
    display_name: string | null;
    avatar_url: string | null;
  };
  last_message?: {
    content_text: string;
    sender_id: string;
    created_at: string;
  };
  unread_count?: number; // Number of unread messages for current user
};

export type Message = {
  id: string;
  conversation_id: string;
  sender_id: string;
  content_text: string;
  type: 'text';
  created_at: string;
  seen_at: string | null;
};

type RawJoin<T> = T | T[];
type RawConversationRow = {
  id: string;
  listing_id: string;
  buyer_id: string;
  seller_id: string;
  blocked_by: string | null;
  last_message_at: string | null;
  created_at: string;
  listing: RawJoin<{ title: string; slug: string | null }>;
  buyer: RawJoin<{ display_name: string | null; avatar_url: string | null }>;
  seller: RawJoin<{ display_name: string | null; avatar_url: string | null }>;
};

export const MESSAGES_READ_EVENT = 'chat:messages-read';

export async function getUserConversations(userId: string): Promise<Conversation[]> {
  const supabase = createClient();
  
  const { data, error } = await supabase
    .from('conversations')
    .select(`
      id,
      listing_id,
      buyer_id,
      seller_id,
      blocked_by,
      last_message_at,
      created_at,
      listing:listings!conversations_listing_id_fkey(title, slug),
      buyer:profiles!conversations_buyer_id_fkey(display_name, avatar_url),
      seller:profiles!conversations_seller_id_fkey(display_name, avatar_url)
    `)
    .or(`buyer_id.eq.${userId},seller_id.eq.${userId}`)
    .order('last_message_at', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false });

  if (error) {
    // Don't throw error for normal "no conversations found" or transient schema cache scenarios.
    if (
      error.code === 'PGRST116' ||
      error.code === 'PGRST002' ||
      error.message?.includes('schema cache') ||
      error.message?.includes('relation') ||
      error.message?.includes('profiles_public')
    ) {
      console.warn('Returning empty conversations due to:', error.message);
      return [];
    }
    console.error('getUserConversations error:', error);
    throw error;
  }

  // Normalize one-to-one joins that can come back as arrays
  const base = ((data || []) as RawConversationRow[]).map((conversation) => ({
    ...conversation,
    listing: Array.isArray(conversation.listing) ? conversation.listing[0] : conversation.listing,
    buyer: Array.isArray(conversation.buyer) ? conversation.buyer[0] : conversation.buyer,
    seller: Array.isArray(conversation.seller) ? conversation.seller[0] : conversation.seller,
  }));

  // Fetch last messages and unread counts for all conversations in one query (no N+1)
  const ids = base.map((c) => c.id);
  const lastByConv = new Map<string, { content_text: string; sender_id: string; created_at: string }>();
  const unreadCountByConv = new Map<string, number>();
  
  if (ids.length) {
    // Get last messages
    const { data: msgs, error: mErr } = await supabase
      .from('messages')
      .select('conversation_id, content_text, sender_id, created_at')
      .in('conversation_id', ids)
      .order('created_at', { ascending: false });
    if (mErr) throw mErr;
    
    for (const m of (msgs || []) as Array<{ conversation_id: string; content_text: string; sender_id: string; created_at: string }>) {
      if (!lastByConv.has(m.conversation_id)) {
        lastByConv.set(m.conversation_id, {
          content_text: m.content_text,
          sender_id: m.sender_id,
          created_at: m.created_at,
        });
      }
    }

    // Get unread message counts - messages sent by others that haven't been seen by current user
    const { data: unreadMsgs, error: unreadErr } = await supabase
      .from('messages')
      .select('conversation_id')
      .in('conversation_id', ids)
      .neq('sender_id', userId) // Messages not sent by current user
      .is('seen_at', null); // That haven't been seen yet
    
    if (unreadErr) throw unreadErr;
    
    // Count unread messages per conversation
    for (const msg of (unreadMsgs || [])) {
      const currentCount = unreadCountByConv.get(msg.conversation_id) || 0;
      unreadCountByConv.set(msg.conversation_id, currentCount + 1);
    }
  }

  return base.map((c) => ({
    ...c,
    last_message: lastByConv.get(c.id),
    unread_count: unreadCountByConv.get(c.id) || 0,
  }));
}

export async function getConversationMessages(conversationId: string): Promise<Message[]> {
  const supabase = createClient();
  
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data || [];
}

// Fetch a window of messages (latest first), with optional cursor and limit.
// Returns messages in ASCENDING order for UI, along with fetched count (to infer hasMore when fetched > limit).
export async function getConversationMessagesPaged(
  conversationId: string,
  options?: { before?: string; limit?: number }
): Promise<{ messages: Message[]; fetched: number }> {
  const supabase = createClient();
  const limit = options?.limit ?? 50;
  const limitPlusOne = limit + 1;

  let query = supabase
    .from('messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: false })
    .limit(limitPlusOne);

  if (options?.before) {
    query = query.lt('created_at', options.before);
  }

  const { data, error } = await query;
  if (error) throw error;

  const desc = (data || []) as Message[]; // newest first
  const fetched = desc.length;
  const asc = desc.slice(0, limit).reverse();

  return { messages: asc, fetched };
}

export async function sendMessage(conversationId: string, senderId: string, content: string): Promise<Message> {
  const supabase = createClient();
  
  // Insert message
  const { data: message, error: messageError } = await supabase
    .from('messages')
    .insert({
      conversation_id: conversationId,
      sender_id: senderId,
      content_text: content,
      type: 'text'
    })
    .select()
    .single();

  if (messageError) throw messageError;

  // Update conversation last_message_at
  const { error: updateError } = await supabase
    .from('conversations')
    .update({ last_message_at: new Date().toISOString() })
    .eq('id', conversationId);

  if (updateError) {
    console.error('Conversation timestamp update error:', updateError);
  }

  // Trigger OneSignal notifications via Supabase Edge Function
  // Run on both server and client – the Supabase client will attach the caller's JWT.
  void supabase.functions
    .invoke('send-notification', {
      body: {
        conversationId,
        senderId,
        messageContent: content
      }
    })
    .then(({ error: notificationError }) => {
      if (notificationError) {
        console.error('Edge function notification error:', notificationError);
      }
    })
    .catch((notificationError) => {
      console.error('Failed to trigger notifications:', notificationError);
    });

  return message;
}

export async function createConversation(listingId: string, buyerId: string, sellerId: string): Promise<Conversation> {
  const supabase = createClient();
  
  // Check if conversation already exists
  const { data: existing } = await supabase
    .from('conversations')
    .select('id')
    .eq('listing_id', listingId)
    .eq('buyer_id', buyerId)
    .eq('seller_id', sellerId)
    .single();

  if (existing) {
    // Return existing conversation
    const conversations = await getUserConversations(buyerId);
    return conversations.find(c => c.id === existing.id)!;
  }

  // Create new conversation
  const { data, error } = await supabase
    .from('conversations')
    .insert({
      listing_id: listingId,
      buyer_id: buyerId,
      seller_id: sellerId
    })
    .select(`
      id,
      listing_id,
      buyer_id,
      seller_id,
      blocked_by,
      last_message_at,
      created_at,
      listing:listings(title, slug),
      buyer:profiles!conversations_buyer_id_fkey(display_name, avatar_url),
      seller:profiles!conversations_seller_id_fkey(display_name, avatar_url)
    `)
    .single();

  if (error) throw error;
  
  return {
    ...data,
    listing: Array.isArray(data.listing) ? data.listing[0] : data.listing,
    buyer: Array.isArray(data.buyer) ? data.buyer[0] : data.buyer,
    seller: Array.isArray(data.seller) ? data.seller[0] : data.seller,
    last_message: undefined
  };
}

export async function markMessagesAsRead(conversationId: string, userId: string): Promise<void> {
  const supabase = createClient();
  
  const { error } = await supabase
    .from('messages')
    .update({ seen_at: new Date().toISOString() })
    .eq('conversation_id', conversationId)
    .neq('sender_id', userId)
    .is('seen_at', null);

  if (error) throw error;

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(MESSAGES_READ_EVENT, {
        detail: {
          conversationId,
          userId,
        },
      })
    );
  }
}

export function getOtherParticipant(conversation: Conversation, currentUserId: string) {
  return conversation.buyer_id === currentUserId ? conversation.seller : conversation.buyer;
}

// Create or get existing conversation for a listing
export async function createOrGetConversation(
  listingId: string, 
  buyerId: string, 
  sellerId: string
): Promise<{ conversationId: string; isNew: boolean }> {
  const supabase = createClient();

  // Check if conversation already exists
  const { data: existingConversation } = await supabase
    .from('conversations')
    .select('id')
    .eq('listing_id', listingId)
    .eq('buyer_id', buyerId)
    .eq('seller_id', sellerId)
    .maybeSingle();

  if (existingConversation) {
    return { conversationId: existingConversation.id, isNew: false };
  }

  // Create new conversation
  const { data, error } = await supabase
    .from('conversations')
    .insert({
      listing_id: listingId,
      buyer_id: buyerId,
      seller_id: sellerId
    })
    .select('id')
    .single();

  if (error) throw error;

  return { conversationId: data.id, isNew: true };
}
